import { type ClassValue, clsx } from 'clsx'
import { extendTailwindMerge } from 'tailwind-merge'

/**
 * DS2 renames most of Tailwind's font sizes — `text-body`, `text-small`,
 * `text-label`, `text-h1..h3`, `text-caption`, `text-micro`, `text-display`.
 * tailwind-merge only knows the DEFAULT names, so it read every one of those as
 * a text-COLOUR utility and deleted whichever colour or size came first in the
 * same `cn()` call:
 *
 *   cn('text-[var(--text-public-primary)]', 'text-body')  →  'text-body'
 *   cn('text-body', 'text-[var(--text-primary)]')         →  'text-[var(--text-primary)]'
 *
 * Measured, not theorised. It is why the EventRipple wordmark never rendered its
 * brand gradient, why that wordmark came out #151A23 on #090B11 — 1.1:1 — on the
 * always-dark public header in light mode, and why the bottom-nav labels have
 * been drawing at an inherited 15px instead of their own size.
 *
 * Registering the DS2 names in the `font-size` group makes sizes conflict with
 * sizes and colours survive beside them, which is what every call site already
 * assumed. One fix at the root, rather than argument order per call.
 */
const twMerge = extendTailwindMerge({
  extend: {
    classGroups: {
      'font-size': [
        // marketing-*: the landing's poster roles (index.css, 31.10.2026); unregistered they were read
        // as colours and deleted beside the heading's ink, so the hero rendered at body size.
        { text: ['display', 'h1', 'h2', 'h3', 'body', 'label', 'small', 'caption', 'micro', 'marketing-hero', 'marketing-section', 'marketing-lead'] },
      ],
    },
  },
})

/**
 * Merge class names, letting the LAST conflicting utility win.
 *
 * clsx alone only concatenates, so two utilities from the same group both
 * survive and the winner is decided by their order in the generated
 * stylesheet, not by the caller. That silently defeated overrides — e.g.
 * `<Button className="hidden">` kept `display: inline-flex` from the Button's
 * own base class (Tailwind emits `.inline-flex` after `.hidden`), so
 * "desktop-only" buttons stayed visible on mobile.
 *
 * twMerge resolves those conflicts by group, so a consumer's class reliably
 * overrides the component's default.
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Convert an event/offer title to a URL-friendly slug.
 *
 * Used to make share URLs human-readable:
 *   /event/public/<token>/<slug>
 *
 * The token remains the lookup key; the slug is decoration only. Norwegian
 * characters are transliterated rather than stripped (Bekkjarvik → bekkjarvik,
 * Påmelding → paamelding) so the URL still reads as the original word.
 */
export function titleToSlug(title: string | null | undefined): string {
  if (!title) return ''
  return title
    .toLowerCase()
    .normalize('NFKD')                          // decompose accented chars
    .replace(/æ/g, 'ae')
    .replace(/ø/g, 'oe')
    .replace(/å/g, 'aa')
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/[̀-ͯ]/g, '')            // strip remaining diacritics
    .replace(/[^a-z0-9]+/g, '-')                // non-alphanumeric → dashes
    .replace(/^-+|-+$/g, '')                    // trim leading/trailing dashes
    .slice(0, 60)                               // cap length
    .replace(/-+$/, '')                         // re-trim if slice cut mid-word
}
